import { useT } from "@/lib/i18n/use-t";
import "react-image-crop/dist/ReactCrop.css";

import { useQueryClient } from "@tanstack/react-query";
import { Camera } from "lucide-react";
import { useCallback, useRef, useState, useTransition } from "react";
import ReactCrop, { type Crop, centerCrop, makeAspectCrop } from "react-image-crop";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { postFormData } from "@/lib/api/_client";
import { authClient } from "@/lib/auth-client";
import { T } from "./profile-picture.i18n";

type ProfilePictureProps = {
  currentImage: string | null;
  userName: string;
};

function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

export function ProfilePicture({ currentImage, userName }: ProfilePictureProps) {
  const t = useT(T);
  const queryClient = useQueryClient();
  const [isPending, startTransition] = useTransition();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [error, setError] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const initials = userName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      setError(t.selectPngOrJpg);
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError(t.imageTooLarge);
      return;
    }

    setError(null);
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
      setDialogOpen(true);
    };
    reader.readAsDataURL(file);

    // Reset input so same file can be selected again
    e.target.value = "";
  };

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, 1));
  }, []);

  const getCroppedImg = async (): Promise<Blob | null> => {
    const image = imgRef.current;
    if (!image || !crop) return null;

    const canvas = document.createElement("canvas");
    const _scaleX = image.naturalWidth / image.width;
    const _scaleY = image.naturalHeight / image.height;

    // Set canvas size to desired output (600x600 for avatar + PDF use at 5cm/300 DPI)
    const outputSize = 600;
    canvas.width = outputSize;
    canvas.height = outputSize;

    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    // Calculate crop dimensions in natural image coordinates
    const cropX = (crop.x / 100) * image.naturalWidth;
    const cropY = (crop.y / 100) * image.naturalHeight;
    const cropWidth = (crop.width / 100) * image.naturalWidth;
    const cropHeight = (crop.height / 100) * image.naturalHeight;

    ctx.drawImage(image, cropX, cropY, cropWidth, cropHeight, 0, 0, outputSize, outputSize);

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        "image/jpeg",
        0.9,
      );
    });
  };

  const handleSave = async () => {
    setError(null);

    const croppedBlob = await getCroppedImg();
    if (!croppedBlob) {
      setError(t.failedToCrop);
      return;
    }

    startTransition(async () => {
      // Upload to S3
      const uploadFormData = new FormData();
      uploadFormData.append("file", croppedBlob, "profile.jpg");

      let url: string;
      try {
        const data = await postFormData<{ url: string }>(
          "/internal/uploads/upload",
          uploadFormData,
        );
        url = data.url;
      } catch (err: any) {
        setError(err.message || t.failedToUpload);
        return;
      }

      // Update user via better-auth (updates both DB and session)
      const result = await authClient.updateUser({ image: url });
      if (result.error) {
        setError(result.error.message || t.failedToSavePhoto);
        return;
      }

      setDialogOpen(false);
      setImageSrc(null);
      setCrop(undefined);
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      queryClient.invalidateQueries({ queryKey: ["me"] });
    });
  };

  const handleClose = () => {
    setDialogOpen(false);
    setImageSrc(null);
    setCrop(undefined);
    setError(null);
  };

  return (
    <>
      <div className="flex items-center gap-4">
        <div className="relative group">
          <Avatar className="h-20 w-20">
            <AvatarImage src={currentImage || undefined} alt={userName} />
            <AvatarFallback className="text-xl">{initials}</AvatarFallback>
          </Avatar>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
          >
            <Camera className="h-6 w-6 text-white" />
          </button>
        </div>
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            {t.changePhoto}
          </Button>
          <p className="text-xs text-muted-foreground mt-1">{t.pngOrJpgMax5MB}</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {error && !dialogOpen && <p className="text-sm text-destructive mt-2">{error}</p>}

      <Dialog open={dialogOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t.cropProfilePicture}</DialogTitle>
            <DialogDescription>{t.dragToReposition}</DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md mb-4">
                {error}
              </div>
            )}

            {imageSrc && (
              <div className="flex justify-center">
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  aspect={1}
                  circularCrop
                >
                  <img
                    ref={imgRef}
                    src={imageSrc}
                    alt={t.cropPreview}
                    onLoad={onImageLoad}
                    className="max-h-[400px]"
                  />
                </ReactCrop>
              </div>
            )}
          </div>

          <DialogFooter>
            <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
            <Button onClick={handleSave} loading={isPending}>
              {t.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
